import React, { useMemo } from "react";
import { Accordion, Box, Text, ActionIcon, ScrollArea, Divider } from "@mantine/core";
import { IconBook, IconExternalLink, IconFileText, IconFile, IconFileTypePdf, IconFileTypeDoc, IconFileTypeTxt, IconFileTypeJpg } from "@tabler/icons-react";
import { useDarkMode } from "usehooks-ts";

function formatSourceUrl(url) {
  const urlObj = new URL(url);
  const getDocumentId = urlObj.pathname.split("/").pop();
  return `${window.origin}/dashboard/documents/${getDocumentId}`;
}

const getFileIcon = (fileType) => {
  switch (fileType) {
    case "pdf":
      return <IconFileTypePdf size={18} color="red" />;
    case "doc":
    case "docx":
      return <IconFileTypeDoc size={18} color="blue" />;
    case "jpg":
    case "jpeg":
    case "png":
      return <IconFileTypeJpg size={18} color="teal" />;
    case "txt":
      return <IconFileTypeTxt size={18} />;
    default:
      return <IconFile size={18} />;
  }
};

export const CitationReferenceList = ({ references }) => {
  const limitedReferences = useMemo(() => references.slice(0, 5), [references]);
  const { isDarkMode } = useDarkMode();

  const returnNullForCitationIfItemIsOneAndHasNoSource = useMemo(() => {
    return limitedReferences.length === 1 && (!limitedReferences[0]?.metadata.source || limitedReferences[0]?.content.text === "");
  }, [limitedReferences]);

  if (references.length === 0 || returnNullForCitationIfItemIsOneAndHasNoSource) {
    return null;
  }

  function formatIncidentData(data) {
    const dateMatches = data.match(/\d{4}\/\d{2}\/\d{2}/g);

    const incidents = data.split(/\n|\r/).filter((line) => line.trim() !== "");

    return incidents
      .map((incident, index) => {
        const parts = incident.split(",");
        if (parts.length < 10) return null;

        return {
          location: incidents[0].startsWith("of") ? `District ${incidents[0]}` : incidents[0] || "Unknown",
          incident: parts.find((p) => p.includes("THEFT") || p.includes("ASSAULT") || p.includes("BURGLARY") || p.includes("HOMICIDE") || p.includes("ROBBERY")) || "Unknown",
          address: parts[8] || "Unknown",
          cluster: parts[14] || "Unknown",
          precinct: parts[16] || "Unknown",
          ward: parts[11] || "Unknown",
          district: parts[10] || "Unknown",
          reported_on: dateMatches?.[index] || "Unknown",
          incident_time: parts[23] && parts[24] ? new Date(parts[23]).toUTCString() + " to " + new Date(parts[24]).toUTCString() : "Unknown",
          shift: parts[5] || "Unknown",
        };
      })
      .filter(Boolean);
  }

  // **Example Usage**
  return (
    <Accordion defaultValue="">
      <Accordion.Item value="citations">
        <Accordion.Control
          style={{
            color: !isDarkMode ? "#1A1A1A" : "#1A1A1A",
            backgroundColor: !isDarkMode ? "#F0F0F0" : "#F0F0F0",
            boxShadow: "4px 4px 6px rgba(0, 0, 0, 0.15)",
            borderBottomRightRadius: "8px",
          }}
        >
          <Box display="flex" alignItems="center">
            <IconBook size={20} style={{ marginRight: "8px", color: !isDarkMode ? "#1A1A1A" : "#1A1A1A" }} />
            <Text size="lg" weight={600} style={{ color: !isDarkMode ? "#1A1A1A" : "#1A1A1A", lineHeight: "20px" }}>
              Citations
            </Text>
          </Box>
        </Accordion.Control>

        <Accordion.Panel style={{ backgroundColor: !isDarkMode ? "#F0F0F0" : "#F0F0F0" }}>
          <ScrollArea h={300} pr="sm" style={{ backgroundColor: !isDarkMode ? "#F0F0F0" : "#F0F0F0" }}>
            {limitedReferences.map((reference, index) => (
              <Box
                key={index}
                p="sm"
                mb="sm"
                sx={(theme) => ({
                  border: `1px solid ${theme.colors.gray[3]}`,
                  borderRadius: theme.radius.sm,
                  backgroundColor: !isDarkMode ? "#F0F0F0" : "#F0F0F0",
                })}
              >
                <Text weight={600} size="md" lineClamp={1}>
                  {reference.metadata?.["x-amz-bedrock-kb-source-uri"].startsWith("s3")
                    ? reference.metadata?.["x-amz-bedrock-kb-source-uri"].split("cwizpdf/")[1]
                    : reference.metadata?.["x-amz-bedrock-kb-source-uri"].split(" - ")[1] || "Untitled"}
                </Text>

                {reference.content.text && reference.metadata?.["x-amz-bedrock-kb-source-uri"].startsWith("s3") ? (
                  <Box>
                    {" "}
                    <Text size="sm" style={{ color: !isDarkMode ? "#000000" : "#000000", lineHeight: "20px" }}>
                      {reference.content.text}
                    </Text>
                  </Box>
                ) : (
                  <div>
                    {formatIncidentData(reference?.content?.text).map((incident, idx) => (
                      <Box key={idx} mt={4}>
                        <Text size="sm" style={{ color: !isDarkMode ? "#000000" : "#000000", lineHeight: "20px" }}>
                          📍 Location: {incident.location}
                        </Text>
                        <Text size="sm" style={{ color: !isDarkMode ? "#000000" : "#000000", lineHeight: "20px" }}>
                          🚔 Incident: {incident.incident}
                        </Text>
                        <Text size="sm" style={{ color: !isDarkMode ? "#000000" : "#000000", lineHeight: "20px" }}>
                          📅 Reported On: {incident.reported_on}
                        </Text>
                        <Text size="sm" style={{ color: !isDarkMode ? "#000000" : "#000000", lineHeight: "20px" }}>
                          📍 District: {incident.district}
                        </Text>
                        {idx !== formatIncidentData(reference?.content?.text).length - 1 && <Divider my={4} />}
                      </Box>
                    ))}
                  </div>
                )}

                {reference.metadata.source && (
                  <Box display="flex" align="center" mt="xs">
                    {getFileIcon(reference.metadata?.type?.toLowerCase())}
                    <ActionIcon component="a" href={formatSourceUrl(reference.metadata.source)} target="_blank" rel="noopener noreferrer" ml={8}>
                      <IconExternalLink size={16} />
                    </ActionIcon>
                  </Box>
                )}
              </Box>
            ))}
          </ScrollArea>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};
